import { useState } from 'react';
import { useDocuments, useDeleteDocument, useUpdateDocument } from '../../hooks/useDocuments.js';
import { useError } from '../shared/ErrorBanner.jsx';
import DocUploadForm, { DOC_KIND_LABELS } from './DocUploadForm.jsx';
import DocEditForm from './DocEditForm.jsx';

// Reused on /documents (crew view), /admin/documents (admin all view).
// `target` = user discord_id, 'all', or undefined (= me).
export default function DocumentsList({ target, title, isAdmin }) {
  const { data: docs = [] } = useDocuments(target);
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-file-text" /> {title}</span>
        <button className="btn-sm primary" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Cancel' : '+ Upload'}
        </button>
      </h3>

      {showForm && (
        <DocUploadForm
          targetUser={target}
          isAdmin={isAdmin}
          onDone={() => setShowForm(false)}
        />
      )}

      {docs.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Label</th>
              {target === 'all' && <th>Crew</th>}
              <th>Expires</th>
              <th>Verified</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <DocRow key={d.id} d={d} isAdmin={isAdmin} showCrew={target === 'all'} />
            ))}
          </tbody>
        </table>
      ) : <div className="loading">No documents yet.</div>}
    </div>
  );
}

function DocRow({ d, isAdmin, showCrew }) {
  const [editing, setEditing] = useState(false);
  const del = useDeleteDocument();
  const update = useUpdateDocument();
  const { show } = useError();

  const expiry = d.expires_at ? new Date(d.expires_at) : null;
  const daysLeft = expiry ? Math.ceil((expiry - Date.now()) / 86400000) : null;
  const expiryClass = daysLeft == null ? '' : daysLeft <= 0 ? 'error' : daysLeft <= 14 ? 'orange' : '';

  const remove = async () => {
    if (!confirm(`Delete "${d.label || d.kind}"?`)) return;
    try { await del.mutateAsync(d.id); } catch (err) { show(err.message); }
  };

  const toggleVerify = async () => {
    try { await update.mutateAsync({ id: d.id, verified: !d.verified_at }); }
    catch (err) { show(err.message); }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={showCrew ? 6 : 5} style={{ padding: 0 }}>
          <DocEditForm doc={d} onDone={() => setEditing(false)} />
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td><span className="badge">{DOC_KIND_LABELS[d.kind] || d.kind}</span></td>
      <td>
        <a href={`/api/documents/${d.id}?download=1`} style={{ color: 'var(--orange)' }}>
          {d.label || d.file_name || d.id}
        </a>
      </td>
      {showCrew && <td>{d.user_name}</td>}
      <td>
        {expiry ? (
          <span className={'badge ' + expiryClass} style={
            expiryClass === 'error' ? { background: 'rgba(147,0,10,0.2)', color: 'var(--error-text)' }
            : expiryClass === 'orange' ? {} : { background: 'var(--surface-container-high)' }
          }>
            {expiry.toLocaleDateString()}
            {daysLeft <= 0 ? ` (expired)` : ` (${daysLeft}d)`}
          </span>
        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td>
        {d.verified_at
          ? <span className="badge orange">✓</span>
          : <span style={{ color: 'var(--text-muted)' }}>no</span>}
      </td>
      <td style={{ textAlign: 'right' }}>
        <button className="btn-sm" onClick={() => setEditing(true)}>Edit</button>{' '}
        {isAdmin && (
          <button className="btn-sm" onClick={toggleVerify}>
            {d.verified_at ? 'Unverify' : 'Verify'}
          </button>
        )}{' '}
        <button className="btn-sm" onClick={remove}>Del</button>
      </td>
    </tr>
  );
}
